import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { ReduxStates } from '../../../redux/reduxTypes';
import { fetchMappings, fetchOptions, postMappings } from '../helpers';

import { ReactComponent as TableArrowIcon } from '../../../assets/icons/graph-table-arrow-icon.svg';
import { ReactComponent as CheckIcon } from '../../../assets/icons/circle-check-icon.svg';
import { ReactComponent as ExclamationIconRed } from '../../../assets/icons/circle-exclamation-red-icon.svg';
import { ReactComponent as ArrowRightIcon } from '../../../assets/icons/chevron-right.svg';
import { ReactComponent as ArrowLeftIcon } from '../../../assets/icons/chevron-left.svg';

import BaseButton from '../../../components/BaseButton';
import { PulseLoader } from 'react-spinners';
import { IdNameObject } from '../../../types';
import MappingOptions from './MappingOptions';
import { useOktaAuth } from '@okta/okta-react';

interface Props {
  setStatus: (arg1: any) => void;
}

const MappingTable = ({ setStatus }: Props): React.ReactElement => {
  const copyData = useSelector((state: ReduxStates) => state.copyData);
  const [dataMappings, setDataMappings] = useState<any[] | null>(null);
  const [sortedDataMappings, setSortedDataMappings] = useState<any[] | null>(null);
  const [slicedDataMappings, setSlicedDataMappings] = useState<any[] | null>(null);
  const [sortFieldHigh, setSortFieldHigh] = useState<boolean>(false);
  const [sortStatusHigh, setSortStatusHigh] = useState<boolean>(false);
  const [activeSort, setActiveSort] = useState<string>('status');
  const [marketOptions, setMarketOptions] = useState<IdNameObject[] | null>(null);
  const [retailerOptions, setRetailerOptions] = useState<IdNameObject[] | null>(null);
  const [brandOptions, setBrandOptions] = useState<IdNameObject[] | null>(null);
  const [categoryOptions, setCategoryOptions] = useState<IdNameObject[] | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [changedItems, setChangedItems] = useState<any | null>(null);
  const [fetching, setFetching] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { oktaAuth } = useOktaAuth();

  const COUNT_PER_PAGE = 15;

  const doMappingsFetch = async () => {
    setFetching(true);
    try {
      const mappingsData = await fetchMappings(oktaAuth);
      setDataMappings(mappingsData.dataMapping);
      setFetching(false);
    } catch (error) {
      console.error(error);
      setStatus({
        state: 'error',
        message: `Something went wrong fetching mappings data. Please try again.`
      });
      setFetching(false);
    }
  };

  const doMappingsPost = async () => {
    setIsSaving(true);
    postMappings({
      dataMapping: changedItems
    })
      .then(() => {
        doMappingsFetch();
        setChangedItems(null);
        window.scrollTo(0, 0);
        setStatus({
          state: 'success',
          message: `Your mapping updates were submitted successfully! These changes will reflect in the filter modal after 2 minutes.`
        });
        setIsSaving(false);
      })
      .catch((error) => {
        console.error(error);
        window.scrollTo(0, 0);
        setStatus({
          state: 'error',
          message: `Something went wrong posting mappings data. Please try again.`
        });
        setIsSaving(false);
      });
  };

  const doOptionsFetch = async () => {
    const newValueObject: IdNameObject[] = [
      {
        id: '0',
        name: copyData?.data_mapping_grid_new_value
      }
    ];
    try {
      const optionsDataMarket = await fetchOptions('market');
      setMarketOptions([...newValueObject, ...optionsDataMarket]);
    } catch (error) {
      console.error(error);
    }

    try {
      const optionsDataRetailer = await fetchOptions('retailer');
      setRetailerOptions([...newValueObject, ...optionsDataRetailer]);
    } catch (error) {
      console.error(error);
    }

    try {
      const optionsDataBrand = await fetchOptions('brand');
      setBrandOptions([...newValueObject, ...optionsDataBrand]);
    } catch (error) {
      console.error(error);
    }

    try {
      const optionsDataCategory = await fetchOptions('category');
      setCategoryOptions([...newValueObject, ...optionsDataCategory]);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (copyData) {
      doMappingsFetch();
      doOptionsFetch();
    }
  }, [copyData]);

  const sortData = () => {
    if (dataMappings) {
      let sortedData = [...dataMappings];
      if (activeSort === 'status') {
        sortedData = sortedData.sort((a, b) => Number(a.status) - Number(b.status));
        if (sortStatusHigh) sortedData.reverse();
      } else {
        sortedData = sortedData.sort((a, b) => a.field.localeCompare(b.field));
        if (!sortFieldHigh) sortedData.reverse();
      }

      setSortedDataMappings(sortedData);
    }
  };

  useEffect(() => {
    if (dataMappings) {
      sortData();
      const total = Math.ceil(dataMappings?.length / COUNT_PER_PAGE);
      setTotalPages(total);
    }
  }, [dataMappings]);

  useEffect(() => {
    sortData();
  }, [sortFieldHigh, sortStatusHigh]);

  useEffect(() => {
    if (sortedDataMappings) {
      const start = COUNT_PER_PAGE * currentPage;
      const end = start + COUNT_PER_PAGE;
      const slicedData = [...sortedDataMappings].slice(start, end);
      setSlicedDataMappings(slicedData);
    }
  }, [currentPage, sortedDataMappings]);

  const mainHeaderStyles =
    'relative text-left w-[15vw] lg:w-[20vw] box-border py-3 font-nunitoRegular border-r border-borderGray text-sm';
  const headerContainerStyles = 'flex items-center justify-between px-3 py-2';
  const dataRowStyles = 'font-nunitoRegular text-sm tracking-[0.2px] py-5';
  const baseIconStyles = '2lg:relative 2lg:right-[15px]';

  const variantStyles = {
    sort: {
      high: 'transform rotate-180'
    }
  };

  const getOptionsForField = (field: string) => {
    let options = null;
    switch (field.toLowerCase()) {
      case 'market':
        options = marketOptions;
        break;
      case 'retailer':
        options = retailerOptions;
        break;
      case 'brand':
        options = brandOptions;
        break;
      case 'category':
        options = categoryOptions;
        break;
    }

    return options;
  };

  const updateChangedItems = (updatedItem: any) => {
    const changedItem = { ...updatedItem };
    delete changedItem['status'];
    const newValue =
      changedItem.mappedValue === copyData?.data_mapping_grid_new_value
        ? changedItem.yourValue
        : changedItem.mappedValue;

    if (changedItems && changedItems.length) {
      const existingItems = [...changedItems];
      const existingItem = existingItems?.filter((item) => item.id === changedItem.id);

      if (existingItem && existingItem.length) {
        existingItem[0].mappedValue = newValue;
        setChangedItems(existingItems);
      } else {
        changedItem['mappedValue'] = newValue;
        setChangedItems([...existingItems, ...[changedItem]]);
      }
    } else {
      //first change
      changedItem['mappedValue'] = newValue;
      setChangedItems([changedItem]);
    }
  };

  const updateChanges = (option: string | null, id: string) => {
    if (slicedDataMappings) {
      const items = [...slicedDataMappings];
      const mappingItem = items?.filter((item) => item.id === id)[0];
      mappingItem['mappedValue'] = option;

      updateChangedItems(mappingItem);
      setSlicedDataMappings(items);
    }
  };

  return (
    <div className="block">
      {isSaving && ( 
        <div className="font-nunitoRegular flex justify-center items-center fixed top-0 left-0 h-screen w-screen bg-green-200 bg-[rgba(27,27,27,0.5)] z-[1000] px-6">
          <PulseLoader size={15} />
        </div>
      )} 
      <div className="w-full bg-white rounded-sm">
        <table className={`w-full border-collapse table-fixed relative`}>
          <tbody>
            <tr className="border-borderGray shadow-tableHeaderShadow">
              <th className={mainHeaderStyles}>
                <div
                  className={`${headerContainerStyles} cursor-pointer `}
                  onClick={() => {
                    setSortFieldHigh(!sortFieldHigh);
                    setActiveSort('field');
                  }}
                >
                  <span>{copyData?.data_mapping_grid_field}</span>
                  <TableArrowIcon className={`${baseIconStyles} ${sortFieldHigh && variantStyles.sort.high}`} />
                </div>
              </th>
              <th className={mainHeaderStyles}>
                <div className={headerContainerStyles}>{copyData?.data_mapping_grid_your_value}</div>
              </th>
              <th className={mainHeaderStyles}>
                <div className={headerContainerStyles}>{copyData?.data_mapping_grid_mapped_value}</div>
              </th>
              <th className={`${mainHeaderStyles}  border-r-0`}>
                <div
                  className={`${headerContainerStyles} cursor-pointer`}
                  onClick={() => {
                    setSortStatusHigh(!sortStatusHigh);
                    setActiveSort('status');
                  }}
                >
                  <span>{copyData?.data_mapping_grid_status}</span>
                  <TableArrowIcon className={`${baseIconStyles} ${sortStatusHigh && variantStyles.sort.high}`} />
                </div>
              </th>
            </tr>
            {slicedDataMappings &&
              slicedDataMappings.map((data) => {
                return (
                  <tr className="border-b border-borderGray box-border" key={data.id}>
                    <td className={`${dataRowStyles} pl-[15px]`}>
                      <p>{data.field}</p>
                    </td>
                    <td className={`${dataRowStyles} pl-[15px]`}>
                      <p>{data.yourValue}</p>
                    </td>
                    <td className={`${dataRowStyles} pl-[15px]`}>
                      <MappingOptions
                        options={getOptionsForField(data.field)}
                        mappedValue={data.mappedValue}
                        mappingId={data.id}
                        updateChanges={updateChanges}
                      />
                    </td>
                    <td className={`${dataRowStyles} text-center`}>
                      <div className="m-auto inline-block">
                        {data.status ? <CheckIcon className="fill-[#D0D0D0]" /> : <ExclamationIconRed />}
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
        <div className={`py-5 px-3 flex ${dataMappings ? `justify-between` : `justify-center`}`}>
          {dataMappings ? ( 
            <BaseButton
              variant={changedItems && !isSaving && changedItems.length ? 'tertiary' : 'disabled'}
              onClick={doMappingsPost}
            >
              <span>{copyData?.data_mapping_grid_save}</span>
            </BaseButton>
          ) : fetching ? (
            <p className="text-filterList text-base">{copyData?.loading}</p>
          ) : (
            <p className="font-nunitoBold text-filterList text-base">{copyData?.no_data}</p>
          )}
          <div className="flex items-center">
            {totalPages > 1 && (
              <React.Fragment>
                <BaseButton
                  variant={currentPage > 0 ? 'plain' : 'plainDisabled'}
                  onClick={() => {
                    if (currentPage > 0) setCurrentPage(0);
                  }}
                >
                  <span>{copyData?.data_mapping_grid_first}</span>
                </BaseButton>
                <BaseButton
                  variant={currentPage > 0 ? 'plain' : 'plainDisabled'}
                  onClick={() => {
                    if (currentPage > 0) setCurrentPage(currentPage - 1);
                  }}
                >
                  <span>
                    <ArrowLeftIcon />
                  </span>
                </BaseButton>
                <div>
                  <p className="font-nunitoLight text-sm">
                    {copyData?.data_mapping_grid_page}: {currentPage + 1} {copyData?.data_mapping_grid_of} {totalPages}
                  </p>
                </div>
                <BaseButton
                  variant={currentPage < totalPages - 1 ? 'plain' : 'plainDisabled'}
                  onClick={() => {
                    if (currentPage < totalPages - 1) setCurrentPage(currentPage + 1);
                  }}
                >
                  <span>
                    <ArrowRightIcon />
                  </span>
                </BaseButton>
                <BaseButton
                  variant={currentPage < totalPages - 1 ? 'plain' : 'plainDisabled'}
                  onClick={() => {
                    if (currentPage < totalPages - 1) setCurrentPage(totalPages - 1);
                  }}
                >
                  <span>{copyData?.data_mapping_grid_last}</span>
                </BaseButton>
              </React.Fragment>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MappingTable;
